import { FocusableElement } from '@react-types/shared';
import {
  ChangeEvent,
  DOMAttributes,
  useCallback,
  useRef,
  useState,
} from 'react';
import {
  AriaTextFieldOptions,
  mergeProps,
  useFocus,
  useTextField,
} from 'react-aria';

import useMergedRefs from './useMergedRefs';

type MergedInputProps<TInput extends HTMLInputElement | HTMLTextAreaElement> =
  React.ComponentPropsWithRef<
    TInput extends HTMLInputElement ? 'input' : 'textarea'
  >;

interface UseInputConfig<
  TInput extends HTMLInputElement | HTMLTextAreaElement,
> {
  inputRef: React.ForwardedRef<TInput>;
  onChange?: (
    event: ChangeEvent<HTMLInputElement> & ChangeEvent<HTMLTextAreaElement>,
    value: string,
  ) => void;
  onBlur?: (e: React.FocusEvent<TInput, Element>) => void;
  onFocus?: (e: React.FocusEvent<TInput, Element>) => void;
  onFocusChange?: (isFocused: boolean) => void;
  textFieldProps: TInput extends HTMLInputElement
    ? AriaTextFieldOptions<'input'>
    : AriaTextFieldOptions<'textarea'>;
}

interface UseInput<TInput extends HTMLInputElement | HTMLTextAreaElement> {
  errorMessageProps: DOMAttributes<FocusableElement>;
  focused: boolean;
  inputProps?: MergedInputProps<TInput>;
  internalRef: React.RefObject<TInput>;
  labelProps:
    | DOMAttributes<FocusableElement>
    | React.LabelHTMLAttributes<HTMLLabelElement>;
  onContainerClick: () => void;
  onContainerFocus: () => void;
  onContainerMouseDown: (e: unknown) => void;
  ref: (instance: TInput | null) => void;
}

/**
 * Provides a common interface that can be used both for react-aria based
 *  inputs and textareas
 */
const useInput = <TInput extends HTMLInputElement | HTMLTextAreaElement>(
  config: UseInputConfig<TInput>,
): UseInput<TInput> => {
  const { onBlur, onChange, onFocus, onFocusChange, inputRef, textFieldProps } =
    config;

  const [focused, setFocused] = useState(false);

  const internalRef = useRef<TInput>(null);
  const ref = useMergedRefs([inputRef, internalRef]);

  const {
    errorMessageProps,
    inputProps: baseInputProps,
    labelProps,
  } = useTextField(textFieldProps, internalRef);

  const { focusProps } = useFocus({
    onBlur,
    onFocus,
    onFocusChange: useCallback(
      (newFocused) => {
        setFocused(newFocused);
        onFocusChange?.(newFocused);
      },
      [onFocusChange],
    ),
  });

  const { onChange: ariaInputOnChange, ...restInputProps } = baseInputProps;

  // it's more standard to call onChange with the event sent from the input
  // element.  react-aria doesn't pass this event back to us - it swallows it
  // and just gives back the new string value.
  //
  // in order to pass the event back up to the parent, `handleChange` is added
  // to the input element.  `handleChange` calls the onChange prop and then also
  // calls the onChange handler that react-aria expects to be added  to the
  // input element.
  //
  // see https://github.com/adobe/react-spectrum/blob/ed2c240fdb735786c474ed8d70127d3659057ba6/packages/%40react-aria/textfield/src/useTextField.ts#L141
  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement> & ChangeEvent<HTMLTextAreaElement>) => {
      onChange?.(e, e.target.value);
      ariaInputOnChange?.(e);
    },
    [ariaInputOnChange, onChange],
  );

  // if user clicks anywhere inside of the input container (not necessarily just
  // the input, which is narrower than the full container when using adornments),
  // focus the input
  const handleContainerClick = useCallback(() => {
    internalRef.current?.focus();
  }, []);

  // onFocus will fire if an adornment is a button and is clicked.  Similar to MUI's
  // inputs, if the input is focused when the button is clicked, keep focus on the input
  // otherwise don't focus the input just becuase of the click
  const handleContainerFocus = useCallback(() => {
    if (focused) {
      internalRef.current?.focus();
    }
  }, [focused]);

  const handleContainerMouseDown = useCallback(
    (e) => {
      // when using adornments, the input does not take up the full width of the
      // container.  In this case, when the input is focused and the user clicks
      // outside of the input (like on an adornment), focus will momentarily leave
      // the input element and then will return to it on mouse up when the container's
      // onClick handler is called.  In order to prevent this momentary loss of focus,
      // prevent default on mousedown if the input is currently focused
      //
      // The event target is checked against the input element ref so that we
      // don't prevent the user from clicking around the input (moving the
      // caret position, highlighting characters, etc.) - which is what would
      // happen if preventDefault() were called without the check.  This block
      // should only "silence" clicks within the input container, not within the
      // input itself.
      //
      // NB: This might not be a great tradeoff between component flexibility and
      // UX.  Remove this logic if preventDefault causes issues downstream.
      if (focused && e.target !== internalRef.current) {
        e.preventDefault();
      }
    },
    [focused],
  );

  const inputProps = mergeProps(
    restInputProps,
    { onChange: handleChange },
    focusProps,
  ) as MergedInputProps<TInput>;

  return {
    errorMessageProps,
    focused,
    inputProps,
    internalRef,
    labelProps,
    onContainerClick: handleContainerClick,
    onContainerFocus: handleContainerFocus,
    onContainerMouseDown: handleContainerMouseDown,
    ref,
  };
};

export default useInput;
